import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";

import { useEffect } from "react";

import { ServiceTicket, SpareIssueOption } from "../types";

interface SpareIssueItem {
  sparePartId: string;

  quantity: number;

  remarks?: string;
}

interface SpareIssuePayload {
  items: SpareIssueItem[];

  remarks?: string;
}

interface SpareIssueDialogProps {
  open: boolean;

  loading?: boolean;

  ticket?: ServiceTicket | null;

  spareParts: SpareIssueOption[];

  hubName?: string;

  mode?: "issue" | "request";

  onClose: () => void;

  onSubmit: (
    payload: SpareIssuePayload
  ) => void;
}

const defaultValues: SpareIssuePayload = {
  items: [
    {
      sparePartId: "",

      quantity: 1,

      remarks: "",
    },
  ],
};

const SpareIssueDialog = ({
  open,
  loading = false,
  ticket,
  spareParts,
  hubName,
  mode = "issue",
  onClose,
  onSubmit,
}: SpareIssueDialogProps) => {
  const methods =
    useForm<SpareIssuePayload>({
      defaultValues,
    });

  const {
    control,
    handleSubmit,
    reset,
  } = methods;

  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control,

    name: "items",
  });

  useEffect(() => {
    reset(defaultValues);
  }, [ticket, reset]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        {mode === "request" ? "Request Spare Parts" : "Issue Spare Parts"}
      </DialogTitle>

      <DialogContent dividers>
        <FormProvider
          {...methods}
        >
          <Stack spacing={3}>
            {ticket && (
              <Grid
                container
                spacing={2}
              >
                <Grid item xs={12} md={4}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Ticket Number
                  </Typography>

                  <Typography fontWeight={600}>
                    {ticket.ticketNumber}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Vehicle
                  </Typography>

                  <Typography fontWeight={600}>
                    {ticket.vehicleNumber}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {mode === "request" ? "Request Hub" : "Issue Hub"}
                  </Typography>

                  <Typography fontWeight={600}>
                    {hubName || ticket.warehouseId}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    Technician
                  </Typography>

                  <Typography fontWeight={600}>
                    {ticket.assignedTechnicianName ||
                      "-"}
                  </Typography>
                </Grid>
              </Grid>
            )}

            {fields.map(
              (field, index) => (
                <Grid
                  container
                  spacing={2}
                  key={field.id}
                  alignItems="center"
                >
                  <Grid item xs={12} md={5}>
                    <Controller
                      name={`items.${index}.sparePartId`}
                      control={control}
                      render={({
                        field,
                      }) => (
                        <TextField
                          {...field}
                          select
                          fullWidth
                          label="Spare Part"
                        >
                          {spareParts.map(
                            (part) => (
                              <MenuItem
                                key={
                                  part.id
                                }
                                value={
                                  part.id
                                }
                                disabled={
                                  part.availableStock <=
                                  0
                                }
                              >
                                {part.name} (
                                {
                                  part.partNumber
                                }
                                ) - Hub Stock:{" "}
                                {
                                  part.availableStock
                                }
                                {part.availableStock <=
                                  0
                                  ? " (not available in this ticket hub)"
                                  : ""}
                              </MenuItem>
                            )
                          )}
                        </TextField>
                      )}
                    />
                  </Grid>
                                  <Grid item xs={12} md={2}>
                    <Controller
                      name={`items.${index}.quantity`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Quantity"
                          inputProps={{
                            min: 1,
                            max: spareParts.find(
                              (part) =>
                                part.id ===
                                methods.watch(`items.${index}.sparePartId`)
                            )?.maxQuantityPerServiceIssue ?? undefined,
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      Available Stock
                    </Typography>

                    <Typography
                      fontWeight={600}
                      color="primary.main"
                    >
                      {(() => {
                        const selected =
                          spareParts.find(
                            (part) =>
                              part.id ===
                              methods.watch(
                                `items.${index}.sparePartId`
                              )
                          );

                        return selected
                          ? selected.availableStock
                          : "-";
                      })()}
                    </Typography>

                    {(() => {
                      const selected = spareParts.find(
                        (part) =>
                          part.id ===
                          methods.watch(`items.${index}.sparePartId`)
                      );

                      return selected?.maxQuantityPerServiceIssue != null ? (
                        <Chip
                          color="warning"
                          label={`Service limit: ${selected.maxQuantityPerServiceIssue}`}
                          size="small"
                          sx={{ mt: 0.75, fontWeight: 700 }}
                        />
                      ) : null;
                    })()}
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Controller
                      name={`items.${index}.remarks`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Remarks"
                          placeholder="Optional"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={1}>
                    <IconButton
                      color="error"
                      disabled={
                        fields.length === 1
                      }
                      onClick={() =>
                        remove(index)
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              )
            )}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() =>
                append({
                  sparePartId: "",
                  quantity: 1,
                  remarks: "",
                })
              }
              sx={{ alignSelf: "flex-start" }}
            >
              Add Spare Part
            </Button>

            <Controller
              name="remarks"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={2}
                  label={mode === "request" ? "Request Remarks" : "Issue Remarks"}
                  placeholder="Optional"
                />
              )}
            />
                      </Stack>
        </FormProvider>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={
            loading ||
            !ticket ||
            fields.length === 0
          }
        >
          {loading
            ? mode === "request"
              ? "Requesting..."
              : "Issuing..."
            : mode === "request"
              ? "Request Spare Parts"
              : "Issue Spare Parts"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpareIssueDialog;
